/**
 * AccesosEmailPreviewModal
 * Vista previa del email de accesos (POST /ventas/accesos-email con preview:true).
 *
 * El HTML NO se replica aquí: lo construye el mismo lambda que envía el correo,
 * así que lo que ve el vendedor es exactamente lo que recibe el cliente.
 */
import React, { useEffect, useState } from "react";
import { X, Loader2, AlertTriangle, Info } from "lucide-react";
import axiosClient from "../../api/axiosClient";
import { accesosEmailErrorMessage, isValidEmail } from "../../utils/accesosEmail";

export default function AccesosEmailPreviewModal({
  open,
  onClose,
  companyId,
  email,
  nombre,
}) {
  const [state, setState] = useState({ status: "idle", data: null, error: null });

  useEffect(() => {
    if (!open) {
      setState({ status: "idle", data: null, error: null });
      return;
    }
    let cancelled = false;
    setState({ status: "loading", data: null, error: null });
    axiosClient
      .post("/ventas/accesos-email", {
        preview: true,
        ...(companyId ? { company_id: companyId } : {}),
        ...(isValidEmail(email) ? { email } : {}),
        ...(nombre ? { nombre } : {}),
      })
      .then((data) => {
        if (!cancelled) setState({ status: "ready", data, error: null });
      })
      .catch((err) => {
        if (!cancelled)
          setState({ status: "error", data: null, error: accesosEmailErrorMessage(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [open, companyId, email, nombre]);

  if (!open) return null;

  const data = state.data;

  return (
    <div className="fixed inset-0 z-[11000] flex items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-[640px] max-h-[92vh] flex flex-col bg-white rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between gap-2 px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100">
          <p className="text-[13px] sm:text-sm font-bold text-gray-900 truncate">
            Vista previa · Email de accesos
          </p>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition flex-shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {state.status === "loading" && (
          <div className="flex items-center justify-center gap-2 py-16 text-gray-500 text-sm">
            <Loader2 size={16} className="animate-spin" />
            Cargando la vista previa…
          </div>
        )}

        {state.status === "error" && (
          <div className="m-5 flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
            <span>{state.error}</span>
          </div>
        )}

        {state.status === "ready" && data && (
          <>
            {data.sample && (
              <div className="flex items-start gap-2 px-4 sm:px-5 py-2.5 sm:py-3 bg-amber-50 border-b border-amber-100 text-[11px] sm:text-[12px] text-amber-800">
                <Info size={14} className="flex-shrink-0 mt-0.5" />
                <span>
                  Ejemplo: al crear los accesos irán el usuario y la contraseña reales de este
                  cliente.
                </span>
              </div>
            )}

            <div className="px-4 sm:px-5 py-2.5 sm:py-3 bg-gray-50 border-b border-gray-100 text-[11px] sm:text-[12px] text-gray-600 space-y-0.5">
              <div className="break-all">
                <span className="text-gray-400">De: </span>
                {data.from}
              </div>
              <div className="break-all">
                <span className="text-gray-400">Para: </span>
                {data.to}
              </div>
              <div className="font-semibold text-gray-900 break-words">{data.subject}</div>
            </div>

            {/* min-h menor en móvil: con 420px fijos, en un teléfono en horizontal
                la cabecera + el aviso + el "De/Para" ya se comen el 92vh y el
                iframe quedaba cortado por el overflow-hidden del contenedor. */}
            <iframe
              title="Vista previa del email de accesos"
              srcDoc={data.html}
              sandbox=""
              className="w-full flex-1 min-h-[240px] sm:min-h-[420px] border-0 bg-white"
            />
          </>
        )}
      </div>
    </div>
  );
}
